import { SubscriptionContract } from './subscription-contract'
import { SubscriptionRequest } from './subscription-request'
import type { SubProfileTarget } from './sub-profile'
import { FieldEnum } from '~/utils/payment-method'
import type { MethodForSubscriptions } from '~/types/api/_atoms/MethodForSubscriptions'

// サブスク契約で許可する決済方法 (wire 側のホワイトリスト)。
// store.enabled_payment_methods (wire) を絞り込む .includes() の対象として使われる。
// satisfies により spec の MethodForSubscriptions から外れた値を書けないようにしている。
const _PermittedWire = ['credit-card'] as const satisfies readonly MethodForSubscriptions[]
export const PermittedPaymentMethods: readonly string[] = _PermittedWire

// subscriptionEntry.selectedPaymentMethod に格納される値の型。
// pages/.../input-payment.vue から `selectPaymentMethod(field.field)` で FieldEnum (camelCase) が
// 流入する設計のため、内部キー側に揃える (wire ではなく FieldEnum)。
// FieldEnum と _PermittedWire の対応整合は utils/payment-method.ts:FieldEnumToValueEnum で担保している。
export type PermittedPaymentMethod = FieldEnum

export interface SubscriptionEntry {
  selectedPaymentMethod?: PermittedPaymentMethod
  subscriptionRequest?: SubscriptionRequest
  subscriptionLinkToken?: string
  // 契約URLに載る新規患者(glen未リンク)用トークン。select-target で選択対象者の
  // かかりつけ確立(related_stores#link)に使う。link_with_store! が当該医院の waiting
  // subscription_request を対象者へ back-fill する(=所有者割当)ため、これがあるときは
  // subscription_requests#link は呼ばない。
  customerLinkToken?: string
  // 定期支払いの対象者(本人/メンバー)。select-target で選択し、紐付け・対象者表示に使う。
  selectedTarget?: SubProfileTarget
  completedSubscriptionContract?: SubscriptionContract
}

const customStorage = storage<SubscriptionEntry>('subscriptionEntry')
const subscriptionEntry = ref(customStorage.getItem()) as Ref<SubscriptionEntry>
watchEffect(() => customStorage.setItem(subscriptionEntry.value))

const input = ({
  selectedPaymentMethod,
  subscriptionRequest,
  subscriptionLinkToken,
  customerLinkToken,
  selectedTarget,
}: SubscriptionEntry) => {
  if (selectedPaymentMethod) subscriptionEntry.value.selectedPaymentMethod = selectedPaymentMethod
  if (subscriptionRequest) subscriptionEntry.value.subscriptionRequest = subscriptionRequest
  if (subscriptionLinkToken) subscriptionEntry.value.subscriptionLinkToken = subscriptionLinkToken
  if (customerLinkToken) subscriptionEntry.value.customerLinkToken = customerLinkToken
  if (selectedTarget) subscriptionEntry.value.selectedTarget = selectedTarget
}

const entry = async () => {
  const subscriptionContract = await useSubscriptionRequest().contractByCreditCard(
    subscriptionEntry.value
  )
  complete()
  subscriptionEntry.value.completedSubscriptionContract = subscriptionContract
}

const reset = () => {
  subscriptionEntry.value = {}
  customStorage.removeItem()
}

const complete = () => {
  reset()

  // 申し込み完了時、決済依頼の個数が必ず変わるので取得処理を挟む。
  // 裏で動かすだけなのでエラーになったとしてもハンドリングしない。
  useOrder().getRequests().catch(() => {})
}

export const useSubscriptionEntry = () => {
  return {
    subscriptionEntry: readonly(subscriptionEntry),
    input,
    entry,
    reset,
    complete,
  }
}
